import { create } from 'zustand'
import { supabase } from '@/lib/supabase'
import type { Database } from '@/types/database'

type Ticket = Database['public']['Tables']['tickets']['Row']
type TicketInsert = Database['public']['Tables']['tickets']['Insert']
type TicketUpdate = Database['public']['Tables']['tickets']['Update']

interface TicketsStore {
  tickets: Ticket[]
  isLoading: boolean
  error: string | null

  // Username management
  username: string | null
  setUsername: (username: string) => void

  // CRUD operations
  fetchTickets: (ticketType?: string) => Promise<void>
  createTicket: (ticket: Omit<TicketInsert, 'created_by'>) => Promise<Ticket | null>
  updateTicket: (id: string, updates: TicketUpdate) => Promise<void>
  deleteTicket: (id: string) => Promise<void>

  // Specific actions
  solveTicket: (id: string, solution: string, confirmedBy: string) => Promise<void>
  confirmTicket: (id: string, confirmedBy: string) => Promise<void>

  // Filters and sorting
  filterStatus: string | null
  filterPriority: string | null
  filterCategory: string | null
  setFilterStatus: (status: string | null) => void
  setFilterPriority: (priority: string | null) => void
  setFilterCategory: (category: string | null) => void
}

export const useTicketsStore = create<TicketsStore>((set, get) => ({
  tickets: [],
  isLoading: false,
  error: null,

  // Initialize username from localStorage
  username: typeof window !== 'undefined' ? localStorage.getItem('ms-tickets-username') : null,

  setUsername: (username: string) => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('ms-tickets-username', username)
    }
    set({ username })
  },

  fetchTickets: async (ticketType = 'emaildrafter') => {
    set({ isLoading: true, error: null })

    try {
      let query = supabase
        .from('tickets')
        .select('*')
        .eq('ticket_type', ticketType)

      // Apply filters
      const { filterStatus, filterPriority, filterCategory } = get()

      if (filterStatus) {
        query = query.eq('status', filterStatus)
      }
      if (filterPriority) {
        query = query.eq('priority', filterPriority)
      }
      if (filterCategory) {
        query = query.eq('category', filterCategory)
      }

      const { data, error } = await query

      if (error) throw error

      // Sort tickets by status priority, then by creation date
      const statusOrder = {
        'open': 1,
        'in_progress': 2,
        'solved': 3,
        'confirmed': 4,
        'closed': 5
      }

      const sortedTickets = (data || []).sort((a, b) => {
        // First sort by status priority
        const statusDiff = (statusOrder[a.status as keyof typeof statusOrder] || 999) -
                          (statusOrder[b.status as keyof typeof statusOrder] || 999)

        if (statusDiff !== 0) return statusDiff

        // Then by creation date (newest first)
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      })

      set({ tickets: sortedTickets, isLoading: false })
    } catch (error) {
      console.error('Error fetching tickets:', error)
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch tickets',
        isLoading: false
      })
    }
  },

  createTicket: async (ticketData) => {
    const { username } = get()
    if (!username) {
      set({ error: 'Username is required to create a ticket' })
      return null
    }

    set({ isLoading: true, error: null })

    try {
      const { data, error } = await supabase
        .from('tickets')
        .insert({
          ...ticketData,
          created_by: username,
          ticket_type: ticketData.ticket_type || 'emaildrafter'
        })
        .select()
        .single()

      if (error) throw error

      // Refresh tickets list
      await get().fetchTickets(ticketData.ticket_type || 'emaildrafter')

      set({ isLoading: false })
      return data
    } catch (error) {
      console.error('Error creating ticket:', error)
      set({
        error: error instanceof Error ? error.message : 'Failed to create ticket',
        isLoading: false
      })
      return null
    }
  },

  updateTicket: async (id: string, updates: TicketUpdate) => {
    set({ isLoading: true, error: null })

    try {
      const { error } = await supabase
        .from('tickets')
        .update(updates)
        .eq('id', id)

      if (error) throw error

      // Update local state
      set(state => ({
        tickets: state.tickets.map(ticket =>
          ticket.id === id ? { ...ticket, ...updates } : ticket
        ),
        isLoading: false
      }))
    } catch (error) {
      console.error('Error updating ticket:', error)
      set({
        error: error instanceof Error ? error.message : 'Failed to update ticket',
        isLoading: false
      })
    }
  },

  deleteTicket: async (id: string) => {
    set({ isLoading: true, error: null })

    try {
      const { error } = await supabase
        .from('tickets')
        .delete()
        .eq('id', id)

      if (error) throw error

      // Update local state
      set(state => ({
        tickets: state.tickets.filter(ticket => ticket.id !== id),
        isLoading: false
      }))
    } catch (error) {
      console.error('Error deleting ticket:', error)
      set({
        error: error instanceof Error ? error.message : 'Failed to delete ticket',
        isLoading: false
      })
    }
  },

  solveTicket: async (id: string, solution: string, confirmedBy: string) => {
    await get().updateTicket(id, {
      status: 'solved',
      solution,
      confirmed_by: confirmedBy as any
    })

    // Trigger email notification
    try {
      const ticket = get().tickets.find(t => t.id === id)
      if (ticket) {
        await fetch('/api/send-ticket-notification', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ticketId: id,
            recipientName: ticket.created_by,
            ticketName: ticket.name,
            solution
          })
        })
      }
    } catch (error) {
      console.error('Error sending notification:', error)
      // Don't throw error as ticket was already updated successfully
    }
  },

  confirmTicket: async (id: string, confirmedBy: string) => {
    await get().updateTicket(id, {
      status: 'confirmed',
      confirmed_by: confirmedBy as any
    })
  },

  // Filters
  filterStatus: null,
  filterPriority: null,
  filterCategory: null,

  setFilterStatus: (status) => {
    set({ filterStatus: status })
    get().fetchTickets()
  },

  setFilterPriority: (priority) => {
    set({ filterPriority: priority })
    get().fetchTickets()
  },

  setFilterCategory: (category) => {
    set({ filterCategory: category })
    get().fetchTickets()
  }
}))

// Helper function to get username with prompt if needed
export const ensureUsername = (): string | null => {
  const { username, setUsername } = useTicketsStore.getState()

  if (!username && typeof window !== 'undefined') {
    const newUsername = prompt('Bitte geben Sie Ihren Namen ein:')
    if (newUsername?.trim()) {
      setUsername(newUsername.trim())
      return newUsername.trim()
    }
    return null
  }

  return username
}